/**
 * Sidebar navigation, per role.
 *
 * These entries are the live views only. The legacy sidebar also linked to
 * several sections whose handler fell through to a "coming soon" placeholder
 * (`switchDoctorTab`'s default branch) — those are dropped.
 */

import { ROLES } from '../../context/AuthContext';
import { ROLE_TITLES } from '../../domain/rbac';

/**
 * The colour a nav item carries.
 *
 * The legacy admin sidebar tinted every section its own hue — red for doctors,
 * cyan for registrations, purple for packages — and the doctor and patient menus
 * coloured their icons the same way (index.html:804–985). It was the fastest way
 * to find a section without reading it, and the rebuild had flattened all of it
 * to one grey.
 *
 * `tint` colours the whole row: background, border and label. `icontint` colours
 * only the glyph and leaves the label grey until hover.
 */
export const NAV_TINTS = {
  red: {
    text: 'text-red-300',
    icon: 'text-red-400',
    bg: 'bg-red-500/10',
    border: 'border-red-500/20',
  },
  cyan: {
    text: 'text-cyan-300',
    icon: 'text-cyan-400',
    bg: 'bg-cyan-500/10',
    border: 'border-cyan-500/20',
  },
  purple: {
    text: 'text-purple-300',
    icon: 'text-purple-400',
    bg: 'bg-purple-500/10',
    border: 'border-purple-500/20',
  },
  orange: {
    text: 'text-orange-300',
    icon: 'text-orange-400',
    bg: 'bg-orange-500/10',
    border: 'border-orange-500/20',
  },
  emerald: {
    text: 'text-emerald-300',
    icon: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/20',
  },
  teal: {
    text: 'text-teal-300',
    icon: 'text-teal-400',
    bg: 'bg-teal-500/10',
    border: 'border-teal-500/20',
  },
  amber: {
    text: 'text-amber-300',
    icon: 'text-amber-400',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/20',
  },
  rose: {
    text: 'text-rose-300',
    icon: 'text-rose-400',
    bg: 'bg-rose-500/10',
    border: 'border-rose-500/20',
  },
};

/**
 * The patient tabs.
 *
 * Hoisted out of the patient menu because three roles need them. An admin and
 * a marketing team member are people who also see doctors, and both hold a real
 * patient record — the routes already admitted them (see app/router.jsx), but
 * only one link into the whole section was ever offered, so the rest was
 * reachable by typing a URL and no other way.
 *
 * Settings is deliberately not here: admins and team members already carry
 * their own settings row, and a second one would be two links to one screen.
 */
const PATIENT_TABS = [
  { to: '/patient', end: true, label: 'الرئيسية', icon: 'layout-grid', icontint: 'cyan' },
  { to: '/patient/doctors', label: 'الأطباء', icon: 'stethoscope', icontint: 'cyan' },
  { to: '/patient/appointments', label: 'مواعيدي', icon: 'calendar-clock', icontint: 'cyan' },
  { to: '/patient/prescriptions', label: 'روشتاتي', icon: 'file-text', icontint: 'purple' },
  { to: '/patient/medications', label: 'أدويتي', icon: 'pill', icontint: 'teal' },
  { to: '/patient/medical-cv', label: 'ملفي الطبي', icon: 'heart-pulse', icontint: 'rose' },
  { to: '/patient/transactions', label: 'معاملاتي المالية', icon: 'banknote', icontint: 'emerald' },
];

/**
 * «ملفي الصحي» (المرحلة 1.2): beside «ملفي الطبي», for the accounts that own a
 * health profile — a patient and a team member, not the admin who also sees
 * the patient's screens.
 */
const HEALTH_TAB = { to: '/patient/health-profile', label: 'ملفي الصحي', icon: 'clipboard-check', icontint: 'rose' };
const withHealth = (tabs) => tabs.flatMap((tab) => (tab.to === '/patient/medical-cv' ? [tab, HEALTH_TAB] : [tab]));

/** Heading row that splits one role’s menu into sections. Not a link. */
const PATIENT_SECTION = { section: 'حسابي كمريض' };

/** The same, above the hospital screens the upgrade added. */
const HOSPITAL_SECTION = { section: 'إدارة المستشفى' };

/**
 * The seven department desks, as the hospital's management sees them.
 *
 * The owner and the co-admin oversee every desk, and the Super Admin working
 * inside a hospital does too, so the same rows are offered to all three. The
 * labels are the department's name rather than each desk's own menu label,
 * because a manager is choosing *which* department, not doing its job.
 */
export const DEPARTMENTS_SECTION = { section: 'أقسام المستشفى' };
/*
 * `roleNeeds` (المرحلة 1.1): the desk's own capability, checked only for an
 * administrative manager who holds one of the ready-made admin roles
 * (lib/adminRoles.js on the server). A finance manager is not shown the lab;
 * a manager without a role sees every desk, as before.
 */
export const DEPARTMENT_ROWS = Object.freeze([
  { to: '/contracts', label: 'التعاقدات والموافقات', icon: 'shield-plus', tint: 'purple', roleNeeds: 'SERVICE_REQUEST_APPROVE' },
  { to: '/accounts', label: 'المحاسب — حسابات المرضى', icon: 'hand-coins', tint: 'emerald', roleNeeds: 'PAYMENT_CONFIRM' },
  { to: '/rooms', label: 'الغرف والعمليات والعناية', icon: 'bed-double', tint: 'rose', roleNeeds: 'ROOMS_MANAGE' },
  { to: '/nursing', label: 'التمريض', icon: 'syringe', tint: 'teal', roleNeeds: 'NURSING_CARE' },
  { to: '/lab', label: 'المعمل', icon: 'flask-conical', tint: 'cyan', roleNeeds: 'LAB_WORK' },
  { to: '/radiology', label: 'الأشعة', icon: 'scan-line', tint: 'purple', roleNeeds: 'RADIOLOGY_WORK' },
  { to: '/pharmacy', label: 'الصيدلية', icon: 'pill', tint: 'amber', roleNeeds: 'PHARMACY_DISPENSE' },
]);

/** Every hospital dashboard, for the Super Admin working inside one. */
export const PLATFORM_HOSPITAL_ROWS = Object.freeze([
  { section: 'داخل المستشفى' },
  { to: '/hospital', label: 'فريق المستشفى', icon: 'shield-check', tint: 'purple' },
  { to: '/reception', label: 'استقبال المرضى', icon: 'clipboard-list', tint: 'cyan' },
  { to: '/operations', label: 'استقبال العيادات', icon: 'layout-dashboard', tint: 'emerald' },
  ...DEPARTMENT_ROWS,
]);

/** The settings row every staff menu ends with. */
const settingsRow = (base) => ({ to: `${base}/settings`, label: 'الإعدادات والأمان', icon: 'shield-check', tint: 'amber' });

export const NAV_ITEMS = {
  [ROLES.PATIENT]: [
    ...withHealth(PATIENT_TABS),
    { to: '/patient/settings', label: 'الإعدادات والأمان', icon: 'shield-check', icontint: 'amber' },
  ],

  [ROLES.DOCTOR]: [
    { to: '/doctor', end: true, label: 'نظرة عامة', icon: 'layout-dashboard', icontint: 'cyan' },
    // One entry, not two. The calendar lives inside the clinics screen — it is
    // coloured by clinic, and the legacy build kept them together for that
    // reason. /doctor/schedule still resolves here, so old links survive.
    { to: '/doctor/clinics', label: 'العيادات', icon: 'map-pin', icontint: 'emerald' },
    // المرحلة 1.3: the patients' requests, and the page patients read about the doctor.
    { to: '/doctor/queue', label: 'طلبات الحجز', icon: 'inbox', icontint: 'cyan' },
    { to: '/doctor/profile', label: 'ملفي المهني', icon: 'badge-check', icontint: 'teal' },
    // The one item with a live indicator instead of an icon, matching the
    // legacy sidebar's "عرض Live" button (index.html:952).
    { to: '/doctor/live', label: 'عرض Live', icon: 'monitor-check', tint: 'emerald', pulse: true },
    { to: '/doctor/prescriptions', label: 'أدويتي', icon: 'pill', icontint: 'purple' },

    /*
     * The private-practice rows.
     *
     * `locks` names the capability the row needs. A hospital-affiliated doctor
     * does not hold PERSONAL_CLINIC_MANAGE, so AppShell renders these greyed
     * and non-navigating, and a click opens the upsell instead — which is what
     * the requirement asks for: visible but disabled, with a way to buy the
     * thing they are missing.
     *
     * Naming the capability here rather than listing the paths in the shell
     * keeps the rule in one place: grant that permission to a doctor and every
     * one of these unlocks at once.
     */
    {
      to: '/doctor/offers',
      label: 'العروض والخصومات',
      icon: 'percent',
      icontint: 'orange',
      locks: 'PERSONAL_CLINIC_MANAGE',
    },
    {
      to: '/doctor/vouchers',
      label: 'أكواد الخصم',
      icon: 'ticket',
      icontint: 'amber',
      locks: 'PERSONAL_CLINIC_MANAGE',
    },
    {
      to: '/doctor/packages',
      label: 'باقاتي التسويقية',
      icon: 'package',
      icontint: 'purple',
      locks: 'PERSONAL_CLINIC_MANAGE',
    },
    {
      to: '/doctor/photography',
      label: 'جلسة تصوير',
      icon: 'camera',
      icontint: 'rose',
      locks: 'PERSONAL_CLINIC_MANAGE',
    },
    { to: '/doctor/settings', label: 'الإعدادات والأمان', icon: 'shield-check', icontint: 'amber' },
  ],

  // The admin menu is fully tinted — every section its own colour, which is how
  // the legacy build made eight near-identical rows scannable at a glance.
  [ROLES.ADMIN]: [
    { to: '/admin', end: true, label: 'إدارة الأطباء', icon: 'users', tint: 'red' },
    {
      to: '/admin/registrations',
      label: 'طلبات التسجيل',
      icon: 'user-check',
      tint: 'cyan',
      badge: 'registrations',
    },
    // المرحلة 1.3: the specialties patients see, and the profiles and reviews waiting.
    { to: '/admin/catalog', label: 'التخصصات والتقييمات', icon: 'list-ordered', tint: 'emerald' },
    { to: '/admin/packages', label: 'باقات الخدمات', icon: 'package', tint: 'purple' },
    {
      to: '/admin/requests',
      label: 'طلبات الباقات',
      icon: 'shopping-cart',
      tint: 'orange',
      badge: 'packageRequests',
    },
    { to: '/admin/institutions', label: 'المنشآت الطبية', icon: 'building-2', tint: 'teal' },
    // The central control: every institution's package, switches and limits.
    { to: '/admin/control', label: 'التحكم المركزي والباقات', icon: 'sliders-horizontal', tint: 'purple' },
    // المرحلة 1.1: the rules for everyone outside an institution, the people
    // who run the platform with the Super Admin, and who opened what.
    { to: '/admin/platform', label: 'سياسات المنصة', icon: 'globe', tint: 'cyan' },
    { to: '/admin/platform-team', label: 'فريق المنصة', icon: 'user-round-check', tint: 'teal' },
    { to: '/admin/audit', label: 'سجل الوصول', icon: 'fingerprint', tint: 'rose' },
    { to: '/admin/photography', label: 'فرق التصوير', icon: 'camera', tint: 'rose' },
    { to: '/admin/accounts', label: 'الحسابات', icon: 'wallet', tint: 'emerald' },
    // The AI grant screen. Purple, and the only admin row that is not a
    // business function — it governs a paid capability, so it sits next to the
    // money rather than in settings.
    { to: '/admin/ai', label: 'الذكاء الاصطناعي', icon: 'sparkles', tint: 'purple' },
    // Messages from users, with screenshots — answered by people, not the AI.
    { to: '/admin/support', label: 'الدعم الفني', icon: 'headset', tint: 'cyan' },
    { to: '/admin/team', label: 'إدارة الفريق', icon: 'users', tint: 'teal' },
    { to: '/admin/appointments', label: 'الحجوزات', icon: 'calendar-check', tint: 'cyan' },
    // The two marks every screen draws, and the platform's name.
    { to: '/admin/branding', label: 'هوية المنصة والشعارات', icon: 'palette', tint: 'purple' },
    { to: '/admin/settings', label: 'الإعدادات والأمان', icon: 'settings', tint: 'amber' },
    PATIENT_SECTION,
    ...PATIENT_TABS,
  ],

  /**
   * A hospital, polyclinic or lab centre manager.
   *
   * Deliberately not the doctor menu with extras bolted on. A manager does
   * not hold a clinic or write prescriptions; they run a catalogue, a roster
   * and a ledger. The one thing they share with a doctor is a calendar, and
   * even that is of service bookings rather than consultations.
   */
  [ROLES.INSTITUTION]: [
    { to: '/institution', end: true, label: 'لوحة المنشأة', icon: 'layout-dashboard', tint: 'cyan' },
    { to: '/institution/services', label: 'الخدمات والأسعار', icon: 'package', tint: 'purple' },
    // المرحلة 1.35: the institution's own price list, where the Super Admin opened it.
    { to: '/institution/price-list', label: 'قائمة أسعار الخدمات', icon: 'tag', tint: 'emerald', center: 'priceList' },
    { to: '/institution/bookings', label: 'حجوزات الخدمات', icon: 'calendar-check', tint: 'emerald' },
    { to: '/institution/members', label: 'الأطباء والموظفون', icon: 'users', tint: 'teal' },
    { to: '/institution/catalog', label: 'التخصصات والأطباء', icon: 'list-ordered', tint: 'emerald' },
    { to: '/institution/finance', label: 'الحسابات والإيرادات', icon: 'banknote', tint: 'amber' },
    { to: '/institution/audit', label: 'سجل الوصول', icon: 'fingerprint', tint: 'rose' },
    { to: '/institution/developer', label: 'مفاتيح الربط (API)', icon: 'code', tint: 'purple' },
    { to: '/institution/settings', label: 'الإعدادات والأمان', icon: 'shield-check', tint: 'orange' },

    /*
     * The hospital screens.
     *
     * These were the omission that made the whole upgrade invisible. The router
     * admits ROLES.INSTITUTION to /hospital, /operations and /reception, and the
     * screens were deployed and working — but nothing in this menu pointed at
     * them, so a hospital manager signed in, saw the six rows above, and had no
     * way to reach any of it short of typing the URL.
     *
     * `/hospital` is the important one: it is the only screen that creates the
     * five staff roles, which is the feature the upgrade exists for.
     *
     * The section heading separates them from the rows above because they are a
     * different job — the rows above run the catalogue and the money, these run
     * the building.
     */
    HOSPITAL_SECTION,
    { to: '/hospital', label: 'فريق المستشفى', icon: 'shield-check', tint: 'purple' },
    { to: '/operations', label: 'استقبال العيادات', icon: 'layout-dashboard', tint: 'emerald' },
    { to: '/reception', label: 'استقبال المرضى', icon: 'clipboard-list', tint: 'cyan' },
    DEPARTMENTS_SECTION,
    ...DEPARTMENT_ROWS,
  ],

  [ROLES.TEAM_MEMBER]: [
    { to: '/team', end: true, label: 'مهامي', icon: 'check-square', icontint: 'cyan' },
    { to: '/team/settings', label: 'الإعدادات والأمان', icon: 'shield-check', icontint: 'amber' },
    PATIENT_SECTION,
    ...withHealth(PATIENT_TABS),
  ],

  /**
   * The four hospital staff menus.
   *
   * Each is deliberately short. A receptionist with a queue in front of them
   * needs one screen, not a tree — and a menu that offers a row the account
   * will be refused is worse than one that does not, because the refusal
   * arrives after the click.
   *
   * Fully tinted, like the admin menu, because these are operational screens
   * scanned at speed rather than read.
   */
  /*
   * `needs` hides a row the account does not hold the capability for. The
   * revenue and the log are `roleOnly`: they are the finance and compliance
   * roles' own screens (المرحلة 1.1), so an administrative manager on no named
   * role keeps the menu it always had.
   */
  [ROLES.CO_ADMIN]: [
    { to: '/hospital', end: true, label: 'فريق المنشأة', icon: 'users', tint: 'purple', needs: 'HOSPITAL_STAFF_VIEW' },
    { to: '/hospital/operations', label: 'استقبال العيادات', icon: 'layout-dashboard', tint: 'emerald', needsAny: ['QUEUE_OVERSEE', 'SCHEDULE_CONFIGURE'] },
    { to: '/hospital/price-list', label: 'قائمة أسعار الخدمات', icon: 'tag', tint: 'emerald', needsAny: ['HOSPITAL_SERVICES_MANAGE', 'BILLING_MANAGE'], center: 'priceList' },
    { to: '/hospital/finance', label: 'الحسابات والإيرادات', icon: 'banknote', tint: 'amber', needs: 'HOSPITAL_FINANCE_VIEW', roleOnly: true },
    { to: '/hospital/audit', label: 'سجل الوصول', icon: 'fingerprint', tint: 'rose', needs: 'AUDIT_VIEW', roleOnly: true },
    { to: '/hospital/settings', label: 'الإعدادات والأمان', icon: 'shield-check', tint: 'amber' },
    DEPARTMENTS_SECTION,
    ...DEPARTMENT_ROWS,
  ],

  /** The platform's own staff (المرحلة 1.1): the institutions they cover, and the log. */
  [ROLES.PLATFORM_STAFF]: [
    { to: '/platform', end: true, label: 'المنشآت المسندة لي', icon: 'building-2', tint: 'teal' },
    { to: '/platform/audit', label: 'سجل الوصول', icon: 'fingerprint', tint: 'rose' },
    { to: '/platform/settings', label: 'الإعدادات والأمان', icon: 'shield-check', tint: 'amber' },
  ],

  [ROLES.RECEPTIONIST]: [
    { to: '/reception', end: true, label: 'استقبال المرضى', icon: 'clipboard-list', tint: 'cyan' },
    { to: '/reception/settings', label: 'الإعدادات والأمان', icon: 'shield-check', tint: 'amber' },
  ],

  [ROLES.OPERATIONS_MANAGER]: [
    { to: '/operations', end: true, label: 'استقبال العيادات', icon: 'layout-dashboard', tint: 'emerald' },
    { to: '/operations/settings', label: 'الإعدادات والأمان', icon: 'shield-check', tint: 'amber' },
  ],

  [ROLES.SECRETARY]: [
    // The one row with a live indicator, matching the doctor's "عرض Live".
    { to: '/secretary', end: true, label: 'طابور الكشف', icon: 'monitor-check', tint: 'teal', pulse: true },
    { to: '/secretary/settings', label: 'الإعدادات والأمان', icon: 'shield-check', tint: 'amber' },
  ],

  /*
   * The seven department desks. One screen each — the desk's own dashboard,
   * with its work inside it as tabs — and settings. The people at these desks
   * were not asked whether they wanted software; a menu of one is the kindest
   * menu there is.
   */
  [ROLES.CONTRACTS_MANAGER]: [
    { to: '/contracts', end: true, label: 'التعاقدات والموافقات', icon: 'shield-plus', tint: 'purple' },
    settingsRow('/contracts'),
  ],
  [ROLES.ACCOUNTS_MANAGER]: [
    { to: '/accounts', end: true, label: 'المحاسب — حسابات المرضى', icon: 'hand-coins', tint: 'emerald' },
    settingsRow('/accounts'),
  ],
  [ROLES.ROOMS_MANAGER]: [
    { to: '/rooms', end: true, label: 'الغرف والعمليات والعناية', icon: 'bed-double', tint: 'rose' },
    settingsRow('/rooms'),
  ],
  [ROLES.NURSE]: [
    { to: '/nursing', end: true, label: 'التمريض', icon: 'syringe', tint: 'teal', pulse: true },
    settingsRow('/nursing'),
  ],
  [ROLES.LAB_TECHNICIAN]: [
    { to: '/lab', end: true, label: 'المعمل', icon: 'flask-conical', tint: 'cyan' },
    settingsRow('/lab'),
  ],
  [ROLES.RADIOLOGIST]: [
    { to: '/radiology', end: true, label: 'الأشعة', icon: 'scan-line', tint: 'purple' },
    settingsRow('/radiology'),
  ],
  [ROLES.PHARMACIST]: [
    { to: '/pharmacy', end: true, label: 'الصيدلية', icon: 'pill', tint: 'amber' },
    settingsRow('/pharmacy'),
  ],
};

/** The heading the legacy sidebar printed above each role's section. */
export const NAV_SECTION_LABEL = {
  [ROLES.ADMIN]: 'الإدارة',
  [ROLES.DOCTOR]: 'عيادتي',
  [ROLES.TEAM_MEMBER]: 'فريق العمل',
  [ROLES.INSTITUTION]: 'إدارة المنشأة',
  [ROLES.CO_ADMIN]: 'الإدارة',
  [ROLES.RECEPTIONIST]: 'استقبال المرضى',
  [ROLES.OPERATIONS_MANAGER]: 'استقبال العيادات',
  [ROLES.SECRETARY]: 'سكرتارية الطبيب',
  [ROLES.CONTRACTS_MANAGER]: 'التعاقدات والموافقات',
  [ROLES.ACCOUNTS_MANAGER]: 'المحاسب',
  [ROLES.ROOMS_MANAGER]: 'الغرف',
  [ROLES.NURSE]: 'التمريض',
  [ROLES.LAB_TECHNICIAN]: 'المعمل',
  [ROLES.RADIOLOGIST]: 'الأشعة',
  [ROLES.PHARMACIST]: 'الصيدلية',
  [ROLES.PLATFORM_STAFF]: 'فريق المنصة',
};

/**
 * The title under the signed-in name. The Arabic comes from ROLE_TITLES — the
 * same words the server stores in Roles.displayName — so a renamed job reads
 * the same in the sidebar, the staff roster and the database. AppShell prefers
 * the title the server returned with the session's capabilities and falls back
 * to this while that answer is on its way.
 */
export const ROLE_LABELS = Object.fromEntries(
  Object.entries(ROLE_TITLES).map(([role, title]) => [role, title]),
);

export const navItemsFor = (role) => NAV_ITEMS[role] || [];

/** The rows an account's capabilities open (domain/menu.js, unit-tested there). */
export { permittedNavItems } from '../../domain/menu';

/**
 * A stable React key for a menu row.
 *
 * Section headings carry no `to`, and keying every row by `item.to` gave them
 * all the same undefined key.
 */
export const navKey = (item) => item.to || `section:${item.section}`;
